import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService } from '../../services/api/auth.service';
import type {
  LoginDto,
  CreateUserDto,
  UserResponse,
  LoginResponse
} from '../../types/model/user.model';

/**
 * Estado de autenticación
 */
interface AuthState {
  user: UserResponse | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
}

// Estado inicial
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,
};

// ==================== ASYNC THUNKS ====================

/**
 * Inicializar autenticación desde localStorage
 */
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const user = authService.getCurrentUser();
      const token = localStorage.getItem('token');

      if (user) {
        return { user, token };
      }

      return null;
    } catch (error: any) {
      return rejectWithValue('Error al inicializar autenticación');
    }
  }
);

/**
 * Login de usuario
 */
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginDto, { rejectWithValue }) => {
    try {
      const response: LoginResponse = await authService.login(credentials);

      // Guardar usuario en localStorage
      authService.saveUser(response.user);

      // Si hay token, guardarlo
      if (response.token) {
        localStorage.setItem('token', response.token);
      }

      return response;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error al iniciar sesión';
      return rejectWithValue(message);
    }
  }
);

/**
 * Registro de usuario
 */
export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData: CreateUserDto, { rejectWithValue }) => {
    try {
      const user: UserResponse = await authService.register(userData);

      // Guardar usuario en localStorage
      authService.saveUser(user);

      // Retornar como LoginResponse para consistencia
      return { user, token: null };
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error al registrarse';
      return rejectWithValue(message);
    }
  }
);

/**
 * Logout de usuario
 */
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      authService.logout();
      return null;
    } catch (error: any) {
      return rejectWithValue('Error al cerrar sesión');
    }
  }
);

// ==================== SLICE ====================

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Actualizar usuario manualmente
    setUser: (state, action: PayloadAction<UserResponse>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },

    // Limpiar error
    clearAuthError: (state) => {
      state.error = null;
    },

    // Reset estado
    resetAuthState: () => initialState,
  },
  extraReducers: (builder) => {
    // Initialize Auth
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        if (action.payload) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
        }
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.isAuthenticated = false;
      });

    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token || null;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });

    // Register
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token || null;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Logout
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Exportar acciones
export const { setUser, clearAuthError, resetAuthState } = authSlice.actions;

// Exportar selectores
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectAuthToken = (state: { auth: AuthState }) => state.auth.token;
export const selectIsInitialized = (state: { auth: AuthState }) => state.auth.isInitialized;

// Exportar reducer
export default authSlice.reducer;
